import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getPayments,
  type PaginatedPaymentsResponse,
  type PaymentFilters,
  type PaymentWithRelations,
} from '@/api/payments';
import { getSupplierAddConfig } from '@/api/suppliers';
import { PaymentQuickViewModal } from '@/components/payment-quick-view-modal';
import { PaymentsTable } from '@/components/payments-table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { useFocusReturn } from '@/hooks/use-focus-return';
import { createSortMapper, PAYMENTS_SORT_MAP } from '@/lib/sort-mapping';

const PAYMENTS_RESULT_CACHE_MAX = 20;

function buildPaymentsCacheKey(f: PaymentFilters): string {
  return JSON.stringify({
    search: (f.search ?? '').trim(),
    direction: f.direction,
    status: f.status_id,
    method: f.payment_method_code,
    clientId: f.payer_party_id,
    searchField: f.search_field,
    from_date: f.from_date,
    to_date: f.to_date,
    page: f.page,
    limit: f.limit,
    sort_by: f.sort_by,
    sort_order: f.sort_order,
  });
}

const sortMapper = createSortMapper(PAYMENTS_SORT_MAP);

function buildPaymentsSearchParams(filters: PaymentFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.page && filters.page !== 1) params.set('page', String(filters.page));
  if (filters.limit && filters.limit !== 20) params.set('limit', String(filters.limit));
  if (filters.sort_by && filters.sort_by !== 'created_at') params.set('sort_by', filters.sort_by);
  if (filters.sort_order && filters.sort_order !== 'DESC')
    params.set('sort_order', filters.sort_order);

  if (filters.direction) params.set('direction', filters.direction);
  if (filters.payment_method_code) params.set('payment_method_code', filters.payment_method_code);

  if (filters.payer_party_id) params.set('clientId', filters.payer_party_id);

  if (filters.search) params.set('search', filters.search);
  if (filters.search_field) params.set('search_field', filters.search_field);

  if (filters.status_id !== undefined) {
    const ids = Array.isArray(filters.status_id) ? filters.status_id : [filters.status_id];
    ids.forEach((id) => params.append('status_id', String(id)));
  }

  if (filters.from_date) params.set('from_date', filters.from_date);
  if (filters.to_date) params.set('to_date', filters.to_date);

  return params;
}

export const Payments: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, user } = useKeycloakAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastWrittenQueryRef = useRef<string | null>(null);
  const [externalRefLabel, setExternalRefLabel] = useState<string>('External Ref');
  const [paymentsData, setPaymentsData] = useState<PaginatedPaymentsResponse | undefined>(
    undefined
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quickViewPayment, setQuickViewPayment] = useState<PaymentWithRelations | null>(null);
  const { capture: captureFocus, restore: restoreFocus } = useFocusReturn();
  const hasLoadedRef = useRef(false);
  const resultsCacheRef = useRef(new Map<string, PaginatedPaymentsResponse>());
  const [tableSearchSeed, setTableSearchSeed] = useState<string>(() => {
    const search = searchParams.get('search')?.trim() || '';
    const searchField =
      (searchParams.get('search_field') as 'payee' | 'payer' | 'ref' | null) ?? null;
    return search && searchField ? `${searchField}:${search}` : search;
  });
  const [filters, setFilters] = useState<PaymentFilters>(() => {
    const initial: PaymentFilters = {
      page: Number(searchParams.get('page') || 1) || 1,
      limit: Number(searchParams.get('limit') || 20) || 20,
      sort_by: (searchParams.get('sort_by') || 'created_at').trim() || 'created_at',
      sort_order:
        (((searchParams.get('sort_order') || 'DESC').toUpperCase() as 'ASC' | 'DESC') ?? 'DESC') ||
        'DESC',
    };

    const direction = searchParams.get('direction');
    if (direction === 'DEBIT' || direction === 'CREDIT') {
      initial.direction = direction;
    }

    const method = searchParams.get('payment_method_code')?.trim();
    if (method) {
      initial.payment_method_code = method;
    }

    const statusIds = searchParams
      .getAll('status_id')
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n));
    if (statusIds.length > 0) {
      initial.status_id = statusIds;
    }

    const search = searchParams.get('search')?.trim();
    if (search) {
      initial.search = search;
    }

    const searchField = searchParams.get('search_field');
    if (searchField === 'payee' || searchField === 'payer' || searchField === 'ref') {
      initial.search_field = searchField;
    }

    const clientId = searchParams.get('clientId')?.trim();
    if (clientId) {
      initial.payer_party_id = clientId;
    }

    const fromDate = searchParams.get('from_date')?.trim();
    if (fromDate) initial.from_date = fromDate;
    const toDate = searchParams.get('to_date')?.trim();
    if (toDate) initial.to_date = toDate;

    return initial;
  });

  const fetchPayments = useCallback(async (currentFilters: PaymentFilters) => {
    const cacheKey = buildPaymentsCacheKey(currentFilters);
    const cached = resultsCacheRef.current.get(cacheKey);
    if (cached !== undefined) {
      setPaymentsData(cached);
      setError(null);
      hasLoadedRef.current = true;
      return;
    }

    const isInitialLoad = !hasLoadedRef.current;
    try {
      if (isInitialLoad) {
        setIsLoading(true);
      }
      setError(null);
      const data = await getPayments(currentFilters);
      setPaymentsData(data);
      hasLoadedRef.current = true;
      if (resultsCacheRef.current.size >= PAYMENTS_RESULT_CACHE_MAX) {
        const firstKey = resultsCacheRef.current.keys().next().value;
        if (firstKey !== undefined) {
          resultsCacheRef.current.delete(firstKey);
        }
      }
      resultsCacheRef.current.set(cacheKey, data);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to load payments');
    } finally {
      if (isInitialLoad) {
        setIsLoading(false);
      }
    }
  }, []);

  // Handle filter changes from the DataTable
  const handleFilterChange = useCallback(
    (
      filterKey: string,
      value: string | string[],
      options?: { search_field?: 'payee' | 'payer' | 'ref' }
    ) => {
      if (
        filterKey === 'direction' ||
        filterKey === 'status_id' ||
        filterKey === 'payment_method_code'
      ) {
        resultsCacheRef.current.clear();
      }
      setFilters((prev) => {
        if (filterKey === 'search') {
          const v = typeof value === 'string' ? value : '';
          const searchVal = v.trim();
          const next: PaymentFilters = { ...prev, page: 1 };
          if (searchVal === '') {
            delete next.search;
            delete next.search_field;
            return next;
          }
          next.search = v;
          if (options?.search_field !== undefined) {
            next.search_field = options.search_field;
          } else {
            delete next.search_field;
          }
          return next;
        }

        const next: PaymentFilters = { ...prev, page: 1 };
        if (filterKey === 'direction') {
          const v = typeof value === 'string' ? value.trim() : '';
          if (v === 'DEBIT' || v === 'CREDIT') {
            next.direction = v;
          } else {
            delete next.direction;
          }
          return next;
        }
        if (filterKey === 'payment_method_code') {
          const v = typeof value === 'string' ? value.trim() : '';
          if (v) {
            next.payment_method_code = v;
          } else {
            delete next.payment_method_code;
          }
          return next;
        }
        if (filterKey === 'status_id') {
          const raw = Array.isArray(value) ? value : value ? [value] : [];
          const ids = raw.map((s) => Number(s)).filter((n) => Number.isFinite(n));
          if (ids.length > 0) {
            next.status_id = ids;
          } else {
            delete next.status_id;
          }
          return next;
        }

        return next;
      });
    },
    []
  );

  // Handle sort changes from the DataTable
  const handleSortChange = useCallback((sortBy: string, order: 'asc' | 'desc') => {
    const backendSortBy = sortMapper.toBackend(sortBy);
    if (!backendSortBy) return;
    setFilters((prev) => ({
      ...prev,
      sort_by: backendSortBy,
      sort_order: order.toUpperCase() as 'ASC' | 'DESC',
    }));
  }, []);

  // Handle page changes from the DataTable
  const handlePageChange = useCallback((page: number) => {
    setFilters((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  // Sync URL -> state (supports back/forward and shared links)
  useEffect(() => {
    const current = searchParams.toString();
    if (lastWrittenQueryRef.current && lastWrittenQueryRef.current === current) {
      lastWrittenQueryRef.current = null;
      return;
    }

    const statusIds = searchParams
      .getAll('status_id')
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n));

    setFilters((prev) => {
      const next: PaymentFilters = {
        ...prev,
        page: Number(searchParams.get('page') || 1) || 1,
        limit: Number(searchParams.get('limit') || prev.limit || 20) || prev.limit || 20,
        sort_by:
          (searchParams.get('sort_by') || prev.sort_by || 'created_at').trim() || 'created_at',
        sort_order:
          (((searchParams.get('sort_order') || prev.sort_order || 'DESC').toUpperCase() as
            | 'ASC'
            | 'DESC') ??
            'DESC') ||
          'DESC',
      };

      const direction = searchParams.get('direction');
      if (direction === 'DEBIT' || direction === 'CREDIT') {
        next.direction = direction;
      } else {
        delete next.direction;
      }

      const method = searchParams.get('payment_method_code')?.trim();
      if (method) {
        next.payment_method_code = method;
      } else {
        delete next.payment_method_code;
      }

      if (statusIds.length > 0) {
        next.status_id = statusIds;
      } else {
        delete next.status_id;
      }

      const search = searchParams.get('search')?.trim();
      if (search) {
        next.search = search;
      } else {
        delete next.search;
      }

      const searchField = searchParams.get('search_field');
      if (searchField === 'payee' || searchField === 'payer' || searchField === 'ref') {
        next.search_field = searchField;
      } else {
        delete next.search_field;
      }

      const clientId = searchParams.get('clientId')?.trim();
      if (clientId) {
        next.payer_party_id = clientId;
      } else {
        delete next.payer_party_id;
      }

      const fromDate = searchParams.get('from_date')?.trim();
      if (fromDate) {
        next.from_date = fromDate;
      } else {
        delete next.from_date;
      }
      const toDate = searchParams.get('to_date')?.trim();
      if (toDate) {
        next.to_date = toDate;
      } else {
        delete next.to_date;
      }

      return next;
    });

    const raw = (() => {
      const s = searchParams.get('search')?.trim() || '';
      const f = (searchParams.get('search_field') as 'payee' | 'payer' | 'ref' | null) ?? null;
      return s && f ? `${f}:${s}` : s;
    })();
    setTableSearchSeed(raw);
  }, [searchParams]);

  // Sync state -> URL (so refresh/share preserves the current view)
  useEffect(() => {
    const nextParams = buildPaymentsSearchParams(filters);
    const next = nextParams.toString();
    const current = searchParams.toString();
    if (next === current) return;
    lastWrittenQueryRef.current = next;
    setSearchParams(nextParams, { replace: true });
  }, [filters, searchParams, setSearchParams]);

  useEffect(() => {
    // Only fetch payments if user is authenticated and not loading
    if (isAuthenticated && !authLoading) {
      fetchPayments(filters);
    } else if (!authLoading && !isAuthenticated) {
      setError('Please log in to view payments');
      setIsLoading(false);
    }
  }, [filters, fetchPayments, isAuthenticated, authLoading]);

  useEffect(() => {
    if (!isAuthenticated || authLoading || !user?.partyId) return;
    getSupplierAddConfig(user.partyId)
      .then((config) => {
        setExternalRefLabel(config.externalRefDisplayName?.trim() || 'External Ref');
      })
      .catch(() => {});
  }, [isAuthenticated, authLoading, user?.partyId]);

  const getPaymentLink = (payment: PaymentWithRelations) => ({ to: `/payments/${payment.id}` });

  const handleQuickView = (payment: PaymentWithRelations) => {
    captureFocus();
    setQuickViewPayment(payment);
  };

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-1">
            <h1 className="text-2xl font-bold tracking-tight">Payments</h1>
            <p className="text-sm text-muted-foreground">
              View and manage payments with instructions, participants, and items
            </p>
          </div>
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="payments-from-date" className="text-xs">
                From
              </Label>
              <Input
                id="payments-from-date"
                type="date"
                className="h-9 w-[145px]"
                value={filters.from_date ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setFilters((prev) => {
                    const next = { ...prev, page: 1 };
                    if (v) {
                      next.from_date = v;
                    } else {
                      delete next.from_date;
                    }
                    return next;
                  });
                }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="payments-to-date" className="text-xs">
                To
              </Label>
              <Input
                id="payments-to-date"
                type="date"
                className="h-9 w-[145px]"
                value={filters.to_date ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setFilters((prev) => {
                    const next = { ...prev, page: 1 };
                    if (v) {
                      next.to_date = v;
                    } else {
                      delete next.to_date;
                    }
                    return next;
                  });
                }}
              />
            </div>
            {(filters.from_date || filters.to_date) && (
              <button
                type="button"
                className="mb-0.5 text-xs text-muted-foreground underline hover:text-foreground"
                onClick={() =>
                  setFilters((prev) => {
                    const next = { ...prev, page: 1 };
                    delete next.from_date;
                    delete next.to_date;
                    return next;
                  })
                }
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 lg:px-6">
        <PaymentsTable
          data={paymentsData}
          isLoading={isLoading}
          error={error}
          getRowLink={getPaymentLink}
          onQuickView={handleQuickView}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          currentSortKey={sortMapper.toColumn(filters.sort_by) ?? 'created_at'}
          currentSortOrder={filters.sort_order === 'ASC' ? 'asc' : 'desc'}
          externalRefLabel={externalRefLabel}
          initialSearchInput={tableSearchSeed}
          currentFilterValues={{
            ...(filters.direction ? { direction: filters.direction } : {}),
            ...(filters.payment_method_code
              ? { payment_method_code: filters.payment_method_code }
              : {}),
            ...(filters.status_id !== undefined
              ? {
                  status_id: (Array.isArray(filters.status_id)
                    ? filters.status_id
                    : [filters.status_id]
                  ).map((n) => String(n)),
                }
              : {}),
          }}
        />
      </div>

      <PaymentQuickViewModal
        open={quickViewPayment !== null}
        onClose={() => {
          restoreFocus();
          setQuickViewPayment(null);
        }}
        payment={quickViewPayment}
        externalRefLabel={externalRefLabel}
      />
    </div>
  );
};
